import API_URL, {withSite} from "./api.js";
import messages from "../messages/messages.js";
import {parseServerDate} from "../components/EventCountdown.jsx";

// Human-readable labels for the server-computed 'stage' value. This is the
// one shared mapping used by the events page, the site-wide banner and the
// event page's stage strip.
export const STAGE_LABELS = {
    announced: "Announced",
    signups: "Sign-ups open",
    submissions: "Submissions open",
    voting: "Voting open",
    closed: "Between stages",
    ended: "Ended"
};

// Aftermath label for an ended event: its outcome is what matters, so it
// advertises the results (voting events) or the entries (non-voting).
export function endedLabel(event) {
    return event.voting_enabled
        ? messages["EVENT.STAGE.RESULTS_AVAILABLE"]
        : messages["EVENT.STAGE.ENTRIES_AVAILABLE"];
}

// Display label for an event's current stage. 'ended' resolves to the
// aftermath label instead of the bare "Ended".
export function stageLabel(event) {
    if (event.stage === "ended") return endedLabel(event);
    return STAGE_LABELS[event.stage] || "";
}

// Fetches the public event list (CDN-cached 60s). Resolves
// {events, serverTime}; rejections propagate to the caller.
export function fetchEventList() {
    return fetch(withSite(`${API_URL}/event/list`))
        .then(response => response.json())
        .then(data => {
            if (data.code === 200) {
                return {events: data.data.events || [], serverTime: data.data.server_time};
            }
            return {events: [], serverTime: null};
        });
}

// Milliseconds until the event next needs attention: 0 while any window is
// open, otherwise the time until the nearest future window start, Infinity
// when nothing is open or upcoming (e.g. an ended event with a live banner).
export function activityDistance(event, now) {
    if (event.signup_open || event.submission_open || event.voting_open) return 0;
    let nearest = Infinity;
    for (const key of ["signup_start", "submission_start", "voting_start"]) {
        const date = parseServerDate(event[key]);
        if (date && date.getTime() > now && date.getTime() - now < nearest) {
            nearest = date.getTime() - now;
        }
    }
    return nearest;
}

// Orders events so the one with the nearest current/upcoming activity comes
// first. The list arrives newest-first and the sort is stable, so ties keep
// that order.
export function sortEventsByActivity(events, serverTime) {
    const now = parseServerDate(serverTime)?.getTime() ?? Date.now();
    return [...events].sort((a, b) => activityDistance(a, now) - activityDistance(b, now));
}

// Events the site-wide banner considers "live": banner window active, or any
// signup/submission/voting window open as a fallback. Note an ended event
// with an active banner still counts (aftermath).
export function liveEvents(events) {
    return (events || []).filter(event =>
        event.banner_active || event.signup_open || event.submission_open || event.voting_open);
}

// Picks the event the site-wide banner should advertise, preferring the one
// with the nearest current/upcoming activity. Returns null when no event is
// live.
export function pickBannerEvent(events, serverTime) {
    const live = liveEvents(events);
    if (live.length === 0) return null;
    return sortEventsByActivity(live, serverTime)[0];
}
